import type { Language } from "../i18n";
import { t } from "../i18n";

export interface ViewfinderProps {
  active: boolean;
  isSuccess: boolean;
  isError?: boolean;
  errorMessage?: string | null;
  isOffline?: boolean;
  language?: Language;
  variant?: "barcode" | "priceTag";
}

/**
 * Centered camera viewfinder with animated laser scan line, focus corners,
 * visual feedback (green pulse/flash) on successful barcode detection,
 * error feedback (red pulse/flash with error badge) on invalid EAN or RPC failure,
 * and unobtrusive offline status indicator.
 */
export function Viewfinder({
  active,
  isSuccess,
  isError = false,
  errorMessage = null,
  isOffline = false,
  language = "ru",
  variant = "barcode",
}: ViewfinderProps) {
  if (!active) return null;

  const frameModifier = isError
    ? "viewfinder__frame--error"
    : isSuccess
    ? "viewfinder__frame--success"
    : isOffline
    ? "viewfinder__frame--offline"
    : "";

  const defaultErrorText = t(language, "scan_error_badge");
  const offlineBadgeText = t(language, "offline_badge");
  const scannedBadgeText = t(language, "scanned_badge");

  return (
    <div className="viewfinder" data-testid="viewfinder" aria-hidden="true">
      <div className={`viewfinder__frame${variant === "priceTag" ? " viewfinder__frame--tag" : ""} ${frameModifier}`}>
        <div className="viewfinder__corner viewfinder__corner--tl" />
        <div className="viewfinder__corner viewfinder__corner--tr" />
        <div className="viewfinder__corner viewfinder__corner--bl" />
        <div className="viewfinder__corner viewfinder__corner--br" />
        <div className="viewfinder__laser" />
        {isError && (
          <>
            <div className="viewfinder__flash viewfinder__flash--error" />
            <div className="viewfinder__error-badge" data-testid="viewfinder-error-badge">
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              <span>{errorMessage || defaultErrorText}</span>
            </div>
          </>
        )}
        {isSuccess && !isError && (
          <>
            <div className="viewfinder__flash" />
            <div className="viewfinder__confirmed-badge" data-testid="viewfinder-confirmed-badge">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              <span>{scannedBadgeText}</span>
            </div>
          </>
        )}
        {isOffline && !isSuccess && !isError && (
          <div className="viewfinder__offline-badge" data-testid="viewfinder-offline-badge">
            <span className="viewfinder__offline-dot" />
            <span>{offlineBadgeText}</span>
          </div>
        )}
      </div>
    </div>
  );
}
